import { useEffect, useRef } from "react";

/**
 * Returns a value (or object) that has the specified value(s) from the previous time they changed
 * @param value
 * @example
 * const previousProps = usePrevious({ agEventGuid });
 * if (agEventGuid !== previousProps.agEventGuid) {...
 */
export function usePrevious<T>(value: T): T {
    const ref = useRef(null);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

/**
 * debounce a function call, helpful for inputs that set state & trigger recalculations but might need more time to finish typing
 * @param callback
 * @param delay in milliseconds
 * @example
 * const [value, setValue] = useState<string>(null);
 * const debounceValue = useDebounce((newValue) => setValue(newValue), 500);
 * <TextInput onChange={(value) => debounceValue(value)} />
 */
export function useDebounce<T extends (...args: any) => ReturnType<T>>(callback: T, delay: number) {
    const ref = useRef(null);

    return (...args) => {
        if (ref.current) {
            clearTimeout(ref.current);
        }

        ref.current = setTimeout(() => callback(...args), delay);
    };
}
